import { MouseEventHandler, useCallback } from 'react';
import useLog from './useLog';

const ACADEMY_ORIGIN = 'https://academy.spotgamma.com';

// How long to wait for the handoff before giving up and using the plain link.
const SSO_TIMEOUT_MS = 2_500;

interface AcademySsoResponse {
  url?: string | null;
}

export const isAcademyLink = (link: string): boolean =>
  link.startsWith(ACADEMY_ORIGIN);

const academyPathFor = (link: string): string =>
  link.slice(ACADEMY_ORIGIN.length) || '/';

// Always resolves: the SSO URL if it arrives before the
// timeout (2.5s), the plain `href` on slow, failed, or empty responses.
const bestEffortSsoUrl = (
  fetchSso: (endpoint: string) => Promise<AcademySsoResponse>,
  path: string,
  href: string,
): Promise<string> =>
  Promise.race([
    fetchSso(`v1/academy/sso?path=${encodeURIComponent(path)}`)
      .then((rsp) => rsp?.url ?? href)
      .catch(() => href),
    new Promise<string>((resolve) => {
      setTimeout(() => resolve(href), SSO_TIMEOUT_MS);
    }),
  ]);

// Swaps an Academy click for an SSO handoff so the user lands logged in.
// Defaults to href on failure/timeout.
export const useAcademyOnClick = () => {
  const { fetchAPIWithLog } = useLog('useAcademyLink');

  return useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (e) => {
      // Let the browser handle modified clicks
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }

      const { href, target } = e.currentTarget;

      const path = academyPathFor(href);
      e.preventDefault();

      // Must open synchronously inside the click or popup blockers kill it.
      const tab = target === '_blank' ? window.open('', '_blank') : null;

      void bestEffortSsoUrl(fetchAPIWithLog, path, href).then((url) => {
        if (tab === null) {
          window.location.assign(url);
        } else {
          tab.location.href = url;
        }
      });
    },
    [fetchAPIWithLog],
  );
};
